import classNames from 'classnames';
import React, { ReactElement, Ref, useCallback, useState } from 'react';
import TextAreaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

import '@eika/expandable-input-style';
import { ErrorLabel } from '@eika/label';

export interface ExpandableInputProps extends TextareaAutosizeProps {
    className?: string;
    placeholderText?: string;
    showCharCounter?: boolean;
    error?: boolean | string | ReactElement;
}

const InputError: React.FC<{ error: string | ReactElement }> = ({ error }) =>
    typeof error === 'string' ? <ErrorLabel className="sgw-input__error-label" text={error} /> : <>{error}</>;

const ExpandableInput = React.forwardRef(
    (
        {
            className,
            placeholderText,
            onChange,
            maxLength,
            showCharCounter = false,
            error = false,
            ...rest
        }: ExpandableInputProps,
        ref: Ref<HTMLTextAreaElement>,
    ): JSX.Element => {
        const [hasFocus, setHasFocus] = useState(false);
        const [charLength, setCharLength] = useState(0);

        const limitSize = useCallback(
            (event) => {
                onChange?.({
                    ...event,
                    target: { ...event.target, value: event.target.value.substr(0, maxLength) },
                });
            },
            [maxLength, onChange],
        );

        const handleChange = (event) => {
            if (maxLength !== undefined) {
                limitSize(event);
            } else {
                onChange?.(event);
            }
            setCharLength(event.target.value.length);
        };

        return (
            <>
                <TextAreaAutosize
                    className={classNames(
                        'sgw-expandable-input',
                        { 'sgw-expandable-input__error': !!error },
                        className,
                    )}
                    placeholder={hasFocus ? '' : placeholderText}
                    onFocus={() => setHasFocus(true)}
                    onBlur={() => setHasFocus(false)}
                    maxLength={maxLength}
                    onChange={handleChange}
                    aria-invalid={!!error}
                    ref={ref}
                    {...rest}
                />
                {showCharCounter && maxLength && (
                    <p>
                        {charLength}/{maxLength}
                    </p>
                )}

                {error && typeof error !== 'boolean' && <InputError error={error} />}
            </>
        );
    },
);

export default ExpandableInput;
